import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  useTheme,
  ListItemButton
} from '@mui/material';
import Grid from '@mui/material/Grid'; // TODO: Migrate to Grid2 in a separate task - requires comprehensive updates to all Grid components
import {
  Dashboard as DashboardIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Help as HelpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Add as AddIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Info as InfoIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import LoadingSpinner from '../components/LoadingSpinner';
import useLoading from '../hooks/useLoading';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Add these mock data arrays before the Dashboard component
const incomeCategories = [
  { id: 1, name: 'Salary', percentage: 65, amount: 5200 },
  { id: 2, name: 'Freelance Work', percentage: 15, amount: 1200 },
  { id: 3, name: 'Investments', percentage: 12, amount: 960 },
  { id: 4, name: 'Other', percentage: 8, amount: 640 }
];

const expenseCategories = [
  { id: 1, name: 'Housing', percentage: 35, amount: 1750 },
  { id: 2, name: 'Transportation', percentage: 15, amount: 750 },
  { id: 3, name: 'Food', percentage: 20, amount: 1000 },
  { id: 4, name: 'Utilities', percentage: 10, amount: 500 },
  { id: 5, name: 'Entertainment', percentage: 8, amount: 400 },
  { id: 6, name: 'Other', percentage: 12, amount: 600 }
];

// Add more mock data
const taxDocuments = [
  { id: 1, name: 'W-2 Form', date: '01/31/2023' },
  { id: 2, name: '1099-MISC', date: '01/31/2023' },
  { id: 3, name: 'Property Tax Statement', date: '02/15/2023' },
  { id: 4, name: 'Mortgage Interest Statement', date: '02/01/2023' }
];

const taxCalendar = [
  { id: 1, name: 'Q1 Estimated Tax Payment', date: '04/15/2023' },
  { id: 2, name: 'Federal Tax Filing Deadline', date: '04/15/2023' },
  { id: 3, name: 'Q2 Estimated Tax Payment', date: '06/15/2023' },
  { id: 4, name: 'Q3 Estimated Tax Payment', date: '09/15/2023' }
];

const taxResources = [
  { id: 1, name: 'IRS Tax Guide', description: 'Official IRS tax guide for individuals' },
  { id: 2, name: 'Tax Deduction Checklist', description: 'Comprehensive list of potential tax deductions' },
  { id: 3, name: 'Retirement Accounts Guide', description: 'Tax implications of different retirement accounts' },
  { id: 4, name: 'Small Business Tax Guide', description: 'Tax guidance for small business owners' }
];

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { isLoading, startLoading, stopLoading, withLoading } = useLoading();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [segmentAnchorEl, setSegmentAnchorEl] = useState<null | HTMLElement>(null);
  const [activeSegment, setActiveSegment] = useState<'personal' | 'self-employed' | 'llc' | 's-corp'>('personal');
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    // Simulate loading dashboard data
    const fetchDashboardData = async () => {
      try {
        setDataLoading(true);
        // Simulate API call with delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Mock data
        const mockData = {
          // ... your mock data here
          income: 75000,
          expenses: 25000,
          taxLiability: 15000,
          recentTransactions: [
            { id: 1, description: 'Salary Payment', amount: 5000, date: '2023-03-01' },
            { id: 2, description: 'Office Supplies', amount: -120, date: '2023-02-28' },
            { id: 3, description: 'Client Payment', amount: 2500, date: '2023-02-25' },
          ],
          // ... more mock data
        };
        
        setDashboardData(mockData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setDataLoading(false);
      }
    };

    // Use our loading hook to show loading state
    withLoading(fetchDashboardData());
    
    // Alternative approach using global loading
    // globalLoading.withLoading(fetchDashboardData(), 'Loading your dashboard data...');
  }, [withLoading, activeSegment]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSegmentMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setSegmentAnchorEl(event.currentTarget);
  };

  const handleSegmentMenuClose = () => {
    setSegmentAnchorEl(null);
  };

  const handleSegmentChange = (segment: 'personal' | 'self-employed' | 'llc' | 's-corp') => {
    setActiveSegment(segment);
    handleSegmentMenuClose();
    // This will trigger the useEffect to reload data with the new segment
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Show loading when changing tabs
    startLoading();
    
    // Simulate tab change delay
    setTimeout(() => {
      setTabValue(newValue);
      stopLoading();
    }, 500);
  };

  // Chart data
  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Income',
        data: [3000, 5000, 4500, 6000, 5500, 7000],
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
        tension: 0.4,
      },
      {
        label: 'Expenses',
        data: [2000, 3000, 2800, 3500, 4000, 3800],
        borderColor: theme.palette.error.main,
        backgroundColor: theme.palette.error.light,
        tension: 0.4,
      },
    ],
  };
  
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Income vs Expenses',
      },
    },
  };
  
  // Mock data for recent transactions
  const recentTransactions = [
    { id: 1, date: '2023-06-15', description: 'Client Payment', amount: 1500, type: 'income' },
    { id: 2, date: '2023-06-14', description: 'Office Supplies', amount: -250, type: 'expense' },
    { id: 3, date: '2023-06-12', description: 'Software Subscription', amount: -99, type: 'expense' },
    { id: 4, date: '2023-06-10', description: 'Consulting Services', amount: 2000, type: 'income' },
  ];
  
  // Mock data for tax deadlines
  const taxDeadlines = [
    { id: 1, date: '2023-06-15', description: 'Quarterly Estimated Tax Payment', type: 'federal' },
    { id: 2, date: '2023-07-31', description: 'Form 941 - Employer\'s Quarterly Tax Return', type: 'federal' },
    { id: 3, date: '2023-09-15', description: 'S-Corporation Tax Return Extension Deadline', type: 's-corp' },
    { id: 4, date: '2023-10-16', description: 'Individual Tax Return Extension Deadline', type: 'personal' },
  ];

  // Show loading spinner while data is loading
  if (dataLoading) {
    return (
      <Box sx={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="large" message="Loading your financial dashboard..." />
      </Box>
    );
  }

  // Show a smaller loading spinner when changing tabs
  const tabContent = (
    <>
      {isLoading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <LoadingSpinner size="medium" message="Loading tab content..." />
        </Box>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Income vs Expenses
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <Line options={chartOptions} data={chartData} />
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Upcoming Tax Deadlines
                  </Typography>
                  <List>
                    {taxDeadlines.map((deadline) => (
                      <ListItem key={deadline.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemText
                          primary={deadline.description}
                          secondary={deadline.date}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Chip 
                          size="small" 
                          label={deadline.type} 
                          color={deadline.type === 'federal' ? 'primary' : 'default'}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Recent Transactions
                    </Typography>
                    <Button size="small">View All</Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    {recentTransactions.map((transaction) => (
                      <ListItem key={transaction.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon>
                          {transaction.type === 'income' ? 
                            <AttachMoneyIcon color="success" /> : 
                            <ShoppingCartIcon color="error" />
                          }
                        </ListItemIcon>
                        <ListItemText
                          primary={transaction.description}
                          secondary={transaction.date}
                        />
                        <Typography 
                          variant="body2" 
                          color={transaction.type === 'income' ? 'success.main' : 'error.main'}
                        >
                          {transaction.type === 'income' ? '+' : ''}{transaction.amount.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Total Income
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData?.income.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <ArrowUpwardIcon fontSize="small" /> 8% from last month
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Total Expenses
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData?.expenses.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <ArrowDownwardIcon fontSize="small" /> 5% from last month
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Tax Liability
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData?.taxLiability.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <InfoIcon fontSize="small" /> Estimated for 2023
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Net Savings
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData?.netSavings.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <ArrowUpwardIcon fontSize="small" /> 12% from last month
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<AddIcon />}
                        sx={{ justifyContent: 'flex-start', py: 1 }}
                      >
                        Add New Income
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<AddIcon />}
                        sx={{ justifyContent: 'flex-start', py: 1 }}
                      >
                        Add New Expense
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="outlined"
                        color="info"
                        fullWidth
                        startIcon={<SearchIcon />}
                        sx={{ justifyContent: 'flex-start', py: 1 }}
                      >
                        Search Transactions
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="outlined"
                        color="warning"
                        fullWidth
                        startIcon={<TipsAndUpdatesIcon />}
                        sx={{ justifyContent: 'flex-start', py: 1 }}
                      >
                        Tax Tips
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Income Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    {incomeCategories.map((category) => (
                      <ListItem key={category.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemText
                          primary={category.name}
                          secondary={`${category.percentage}% of total income`}
                        />
                        <Typography variant="body2">
                          {category.amount.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Expense Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    {expenseCategories.map((category) => (
                      <ListItem key={category.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemText
                          primary={category.name}
                          secondary={`${category.percentage}% of total expenses`}
                        />
                        <Typography variant="body2">
                          {category.amount.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Tax Planning Tools
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Tax Deduction Finder
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Find potential tax deductions based on your income and expenses.
                          </Typography>
                          <Button variant="contained" color="primary" size="small">
                            Start Now
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Tax Bracket Calculator
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Calculate your tax bracket and estimated tax liability.
                          </Typography>
                          <Button variant="contained" color="primary" size="small">
                            Calculate
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Retirement Savings
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Plan your retirement savings and tax advantages.
                          </Typography>
                          <Button variant="contained" color="primary" size="small">
                            Plan Now
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Tax Documents
                  </Typography>
                  <List>
                    {taxDocuments.map((doc) => (
                      <ListItem key={doc.id} disablePadding>
                        <ListItemButton>
                          <ListItemIcon>
                            <DescriptionIcon />
                          </ListItemIcon>
                          <ListItemText primary={doc.name} secondary={doc.date} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Tax Calendar
                  </Typography>
                  <List>
                    {taxCalendar.map((event) => (
                      <ListItem key={event.id} disablePadding>
                        <ListItemIcon>
                          <EventIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={event.name} 
                          secondary={event.date}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Tax Resources
                  </Typography>
                  <List>
                    {taxResources.map((resource) => (
                      <ListItem key={resource.id} disablePadding>
                        <ListItemButton>
                          <ListItemIcon>
                            <LinkIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary={resource.name} 
                            secondary={resource.description}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top Navigation Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        p: 2, 
        backgroundColor: theme.palette.primary.main, 
        color: 'white' 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6">US Tax Assistant</Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="inherit">
            <SearchIcon />
          </IconButton>
          <IconButton color="inherit">
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit">
            <HelpIcon />
          </IconButton>
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
            <Avatar src={currentUser?.photoURL || undefined} alt={currentUser?.displayName || 'User'} />
            <Box sx={{ ml: 1 }}>
              <Typography variant="body2">{currentUser?.displayName}</Typography>
              <Button 
                color="inherit" 
                size="small" 
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleMenuClick}
              >
                Account
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
                <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
                <MenuItem onClick={handleMenuClose}>Logout</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {/* Left Sidebar */}
        <Box sx={{ 
          width: 240, 
          backgroundColor: theme.palette.grey[100], 
          borderRight: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ p: 2 }}>
            <Button 
              variant="contained" 
              fullWidth 
              startIcon={<AddIcon />}
              sx={{ mb: 2 }}
            >
              New Transaction
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleSegmentMenuClick}
            >
              {activeSegment === 'personal' ? 'Personal' : 
               activeSegment === 'self-employed' ? 'Self-Employed' :
               activeSegment === 'llc' ? 'LLC' : 'S-Corporation'}
            </Button>
            <Menu
              anchorEl={segmentAnchorEl}
              open={Boolean(segmentAnchorEl)}
              onClose={handleSegmentMenuClose}
            >
              <MenuItem onClick={() => handleSegmentChange('personal')}>Personal</MenuItem>
              <MenuItem onClick={() => handleSegmentChange('self-employed')}>Self-Employed</MenuItem>
              <MenuItem onClick={() => handleSegmentChange('llc')}>LLC</MenuItem>
              <MenuItem onClick={() => handleSegmentChange('s-corp')}>S-Corporation</MenuItem>
            </Menu>
          </Box>
          
          <Divider />
          
          <List>
            <ListItemButton selected>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <AccountBalanceIcon />
              </ListItemIcon>
              <ListItemText primary="Accounts" />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <ReceiptIcon />
              </ListItemIcon>
              <ListItemText primary="Transactions" />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <AttachMoneyIcon />
              </ListItemIcon>
              <ListItemText primary="Tax Planning" />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <BarChartIcon />
              </ListItemIcon>
              <ListItemText primary="Reports" />
            </ListItemButton>
          </List>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <List>
            <ListItemButton>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </List>
        </Box>
        
        {/* Main Content */}
        <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back, {currentUser?.displayName}! Here's an overview of your financial situation.
            </Typography>
          </Box>
          
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 140,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
                  <AttachMoneyIcon sx={{ fontSize: 120 }} />
                </Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Income
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  $75,000
                </Typography>
                <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                  +5.3% from last year
                </Typography>
              </Paper>
            </Grid>
            
            <Grid xs={12} sm={6} md={3}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 140,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
                  <ShoppingCartIcon sx={{ fontSize: 120 }} />
                </Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Expenses
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  $25,000
                </Typography>
                <Typography variant="body2" color="error.main" sx={{ display: 'flex', alignItems: 'center' }}>
                  +2.7% from last year
                </Typography>
              </Paper>
            </Grid>
            
            <Grid xs={12} sm={6} md={3}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 140,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
                  <ReceiptIcon sx={{ fontSize: 120 }} />
                </Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Tax Liability
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  $15,000
                </Typography>
                <Typography variant="body2" color="info.main" sx={{ display: 'flex', alignItems: 'center' }}>
                  Estimated for 2023
                </Typography>
              </Paper>
            </Grid>
            
            <Grid xs={12} sm={6} md={3}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 140,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
                  <AccountBalanceIcon sx={{ fontSize: 120 }} />
                </Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Net Savings
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  $35,000
                </Typography>
                <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                  +12% from last year
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Tabs Section */}
          <Paper elevation={2} sx={{ mb: 4 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Overview" />
              <Tab label="Income" />
              <Tab label="Expenses" />
              <Tab label="Tax Planning" />
              <Tab label="Tax Documents" />
            </Tabs>
            
            {tabContent}
          </Paper>
          
          {/* Tax Tips Section */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tax Tips & Recommendations
            </Typography>
            <Typography variant="body2" paragraph>
              Based on your profile and transaction history, here are some tax optimization recommendations:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Consider Home Office Deduction
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      As a self-employed individual, you may qualify for the home office deduction if you use part of your home exclusively for business.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Maximize Retirement Contributions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contributing to a SEP IRA or Solo 401(k) can reduce your taxable income while building retirement savings.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Track Business Mileage
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Don't forget to track your business mileage for tax deductions. The standard mileage rate for 2023 is 65.5 cents per mile.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Quick Actions Section */}
          <Grid container spacing={2}>
            <Grid xs={12} md={4}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <List>
                    <ListItemButton>
                      <ListItemIcon>
                        <AddIcon />
                      </ListItemIcon>
                      <ListItemText primary="Add New Income" />
                    </ListItemButton>
                    <ListItemButton>
                      <ListItemIcon>
                        <AddIcon />
                      </ListItemIcon>
                      <ListItemText primary="Add New Expense" />
                    </ListItemButton>
                    <ListItemButton>
                      <ListItemIcon>
                        <SearchIcon />
                      </ListItemIcon>
                      <ListItemText primary="Search Transactions" />
                    </ListItemButton>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid xs={12} md={4}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Tax Calendar
                  </Typography>
                  <List>
                    {taxDeadlines.slice(0, 3).map((deadline) => (
                      <ListItem key={deadline.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemText
                          primary={deadline.description}
                          secondary={deadline.date}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid xs={12} md={4}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Tax Tips
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Remember to keep all your receipts for business expenses to maximize deductions.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Consider contributing to your retirement accounts to reduce taxable income.
                  </Typography>
                  <Button size="small" color="primary">
                    View More Tips
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard; 